import type { KeyboardEvent, ReactNode } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { flushSync } from 'react-dom'

import type { ColumnParameters } from '../../contexts/ColumnParametersContext.js'
import { ColumnsVisibilityContext } from '../../contexts/ColumnsVisibilityContext.js'
import { ColumnWidthsContext } from '../../contexts/ColumnWidthsContext.js'
import { useColumnOrderBy, useToggleColumnOrderBy } from '../../contexts/OrderByContext.js'
import { getOffsetWidth } from '../../helpers/width.js'
import { useCellFocus } from '../../hooks/useCellFocus.js'
import { useColumnMenu } from '../../hooks/useColumnMenu.js'
import { useOnCopy } from '../../hooks/useOnCopyToClipboard.js'
import ColumnMenu from '../ColumnMenu/ColumnMenu.js'
import ColumnMenuButton from '../ColumnMenuButton/ColumnMenuButton.js'
import ColumnResizer from '../ColumnResizer/ColumnResizer.js'

interface Props {
  columnIndex: number // index of the column in the dataframe (0-based)
  columnName: string
  columnConfig: Omit<ColumnParameters, 'name' | 'index'> // column configuration, excluding name and index
  children?: ReactNode
  canMeasureWidth?: boolean
  ariaColIndex: number // aria col index for the header
  ariaRowIndex: number // aria row index for the header
  className?: string // optional class name
}

export default function ColumnHeader({ columnIndex, columnName, columnConfig, canMeasureWidth, ariaColIndex, ariaRowIndex, className, children }: Props) {
  // The ref is used to position the menu in handleMenuClick, to measure width, and to focus the cell
  const ref = useRef<HTMLTableCellElement | null>(null)
  const { tabIndex, navigateToCell, focusIfNeeded } = useCellFocus({ ariaColIndex, ariaRowIndex })
  const { isOpen, position, menuId, close, handleMenuClick } = useColumnMenu(ref, navigateToCell)
  const { getHideColumn, showAllColumns } = useContext(ColumnsVisibilityContext)
  const toggleColumnOrderBy = useToggleColumnOrderBy()
  const { sortable } = columnConfig
  const readOnlyOrderBy = toggleColumnOrderBy === undefined
  const { direction, orderByIndex } = useColumnOrderBy(columnName)

  useEffect(() => {
    // inside a useEffect, to warn only once, and not on every render
    if (!sortable && direction !== undefined) {
      console.warn(`Column "${columnName}" has an order in orderBy but is not sortable. Fix the orderBy state or set the column as sortable. The column is not shown as ordered.`)
    }
  }, [sortable, direction, columnName])

  const ariaSort = sortable ? (direction ?? 'none') : undefined
  const canSort = sortable === true && toggleColumnOrderBy !== undefined

  const toggleOrderBy = useMemo(() => {
    if (!toggleColumnOrderBy || !sortable) {
      return undefined
    }
    return () => {
      toggleColumnOrderBy(columnName)
    }
  }, [toggleColumnOrderBy, sortable, columnName])

  // Focus the cell if needed. We use an effect, as it acts on the DOM element after render.
  useEffect(() => {
    focusIfNeeded?.(ref.current)
  }, [focusIfNeeded])

  const handleClick = useCallback(() => {
    navigateToCell?.()
    toggleOrderBy?.()
  }, [toggleOrderBy, navigateToCell])

  const hideColumn = useMemo(() => {
    return getHideColumn?.(columnName)
  }, [getHideColumn, columnName])

  const isMenuEnabled = useMemo(() => {
    const hasCustomMenuGroups = columnConfig.menuGroups && columnConfig.menuGroups.length > 0
    const hideMenu = !sortable && !hideColumn && !showAllColumns && !hasCustomMenuGroups
    return !hideMenu
  }, [sortable, hideColumn, showAllColumns, columnConfig.menuGroups])

  // Get the column width from the context
  const { getStyle, getDataFixedWidth, getWidth, setMeasuredWidth, setFixedWidth, releaseWidth } = useContext(ColumnWidthsContext)
  const columnStyle = getStyle?.(columnIndex)
  const dataFixedWidth = getDataFixedWidth?.(columnIndex)
  const width = getWidth?.(columnIndex)

  const resizeTo = useCallback((value: number) => {
    setFixedWidth?.(columnIndex, value)
  }, [setFixedWidth, columnIndex])

  const tryToMeasureWidth = useCallback(() => {
    const element = ref.current
    if (canMeasureWidth && element && width === undefined && setMeasuredWidth) {
      // use offsetWidth, not clientWidth, to include borders
      const value = getOffsetWidth(element)
      if (isNaN(value)) {
        // browserless unit tests get NaN
        return
      }
      setMeasuredWidth(columnIndex, value)
    }
  }, [canMeasureWidth, setMeasuredWidth, width, columnIndex])

  // Measure default column width when data is ready, if no width is set
  useEffect(() => {
    tryToMeasureWidth()
  }, [tryToMeasureWidth])

  const autoResize = useCallback(() => {
    if (releaseWidth) {
      flushSync(() => {
        releaseWidth(columnIndex)
      })
      tryToMeasureWidth() // TODO(SL): remove and let the effect handle it?
    }
  }, [tryToMeasureWidth, releaseWidth, columnIndex])

  const description = useMemo(() => {
    if (!sortable) {
      return `The column ${columnName} cannot be sorted`
    } else if (readOnlyOrderBy) {
      return `The column ${columnName} order is read-only`
    } else if (orderByIndex !== undefined && orderByIndex > 0) {
      return `Press to sort by ${columnName} in ascending order`
    } else if (direction === 'ascending') {
      return `Press to sort by ${columnName} in descending order`
    } else if (direction === 'descending') {
      return `Press to stop sorting by ${columnName}`
    } else {
      return `Press to sort by ${columnName} in ascending order`
    }
  }, [sortable, readOnlyOrderBy, columnName, direction, orderByIndex])

  const onKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.target !== ref.current || !sortable || !toggleOrderBy) {
      // only handle keyboard events when the header is focused, the column is sortable and can be toggled
      return
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      toggleOrderBy()
    }
  }, [sortable, toggleOrderBy])
  const handleCopy = useOnCopy(columnName)

  // If the hightable user provides a custom header component, they can choose where to place these controls inside it
  const controls = useMemo(() => (
    <div role="group">
      {/**
       * note about the empty space (''):
       * - read-only mode (ariaSort===true, canSort===false): the column could be sorted, but is not currently, so we show the space, as an indication that it could be sorted at some point
       * - not sortable (ariaSort===false): we don't show the space, as an indication that it will never be sorted.
       *
       * note: if the column is not sortable, but is present in orderBy (contradiction), we hide the sort indicator (ariaSort is set to false).
      */}
      {(ariaSort !== undefined)
        && (
          <span role="img" aria-hidden="true">
            {direction === 'ascending' ? '⭡' : direction === 'descending' ? '⭣' : canSort ? '⇅' : ''}
          </span>
        )}
      {isMenuEnabled
        && (
          <ColumnMenuButton
            onClick={handleMenuClick}
            onEscape={navigateToCell}
            tabIndex={tabIndex}
            isExpanded={isOpen}
            menuId={menuId}
            aria-label={`Column menu for ${columnName}`}
          />
        )}
    </div>
  ),
  [ariaSort, direction, isMenuEnabled, handleMenuClick, navigateToCell, tabIndex, isOpen, menuId, columnName, canSort])

  const headerContent = useMemo(() => {
    const { headerComponent } = columnConfig
    if (typeof headerComponent === 'function') {
      return headerComponent(controls)
    }
    return headerComponent ?? children
  }, [columnConfig, controls, children])

  const isFunctionalHeader = useMemo(() => typeof columnConfig.headerComponent === 'function', [columnConfig])

  return (
    <th
      ref={ref}
      scope="col"
      role="columnheader"
      aria-sort={ariaSort}
      data-can-sort={canSort ? 'true' : undefined}
      data-order-by-index={orderByIndex}
      data-functional-header={isFunctionalHeader ? 'true' : undefined}
      aria-label={columnName}
      aria-description={description}
      aria-rowindex={ariaRowIndex}
      aria-colindex={ariaColIndex}
      tabIndex={tabIndex}
      title={description}
      onClick={handleClick}
      onCopy={handleCopy}
      onKeyDown={onKeyDown}
      style={columnStyle}
      className={className}
      data-fixed-width={dataFixedWidth}
    >
      {headerContent}
      {isFunctionalHeader
        ? null
        : isMenuEnabled
          && (
            <ColumnMenuButton
              onClick={handleMenuClick}
              onEscape={navigateToCell}
              tabIndex={tabIndex}
              isExpanded={isOpen}
              menuId={menuId}
              aria-label={`Column menu for ${columnName}`}
            />
          )}
      <ColumnResizer
        resizeTo={resizeTo}
        autoResize={autoResize}
        width={width}
        tabIndex={tabIndex}
        navigateToCell={navigateToCell}
      />
      <ColumnMenu
        columnName={columnName}
        isOpen={isOpen}
        position={position}
        direction={direction}
        sortable={sortable}
        toggleOrderBy={toggleOrderBy}
        hideColumn={hideColumn}
        showAllColumns={showAllColumns}
        close={close}
        id={menuId}
        menuGroups={columnConfig.menuGroups}
      />
    </th>
  )
}
